import { Dispatch } from 'react';

import { CustomerContactTypeEnum } from '../gql/customerGql';
import { Deal } from '../gql/dealGql';
import {
  DealUnreadMessageCountQuery,
  Customer as GenCustomer,
  Deal as GenDeal,
  Message as GenMessage,
  Maybe,
  PhoneType,
} from '../gql/generated/graphql';
import { Message } from '../gql/messageGql';

import { ActiveConversation } from '../components/TextWidget/Components/Message';
import { PersonIdFieldMapKey } from '../constants/notifications';

export type RecipientThreadUnreadCount =
  DealUnreadMessageCountQuery['dealUnreadMessageCount']['byRecipient'][number];

export type ConversationBox = {
  role: PersonIdFieldMapKey;
  personId: Maybe<number>;
  phoneType: PhoneType;
  conversation: ActiveConversation;
};

type ConversationPerson = Pick<
  GenCustomer,
  'id' | 'first_name' | 'last_name' | 'phone_number' | 'home_phone_number'
>;

export const emptyConversation: ActiveConversation = {
  role: '',
  phone_number: '',
  first_name: '',
  last_name: '',
};

export const normalizePhoneNumber = (phoneNumber?: Maybe<string>): string =>
  (phoneNumber ?? '').replace(/\D/g, '').slice(-10);

export const isSamePhoneNumber = ({
  phoneNumber,
  otherPhoneNumber,
}: {
  phoneNumber?: Maybe<string>;
  otherPhoneNumber?: Maybe<string>;
}): boolean => {
  const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
  return (
    !!normalizedPhoneNumber && normalizedPhoneNumber === normalizePhoneNumber(otherPhoneNumber)
  );
};

export const isActiveBox = ({
  box,
  activeConversation,
}: {
  box: ConversationBox;
  activeConversation: ActiveConversation;
}): boolean =>
  box.role === activeConversation.role &&
  isSamePhoneNumber({
    phoneNumber: box.conversation.phone_number,
    otherPhoneNumber: activeConversation.phone_number,
  });

const toBox = ({
  role,
  person,
  phoneType,
  phoneNumber,
}: {
  role: PersonIdFieldMapKey;
  person: ConversationPerson;
  phoneType: PhoneType;
  phoneNumber: string;
}): ConversationBox => ({
  role,
  personId: person.id != null ? Number(person.id) : null,
  phoneType,
  conversation: {
    role,
    phone_number: phoneNumber,
    first_name: person.first_name ?? '',
    last_name: person.last_name ?? '',
  },
});

const toPersonBoxes = ({
  role,
  person,
}: {
  role: PersonIdFieldMapKey;
  person: ConversationPerson;
}): ConversationBox[] => {
  const isHomeSameAsMobile = isSamePhoneNumber({
    phoneNumber: person.home_phone_number,
    otherPhoneNumber: person.phone_number,
  });
  return [
    { phoneType: PhoneType.Mobile, phoneNumber: person.phone_number },
    {
      phoneType: PhoneType.Home,
      phoneNumber: isHomeSameAsMobile ? null : person.home_phone_number,
    },
  ].flatMap(({ phoneType, phoneNumber }) =>
    phoneNumber ? [toBox({ role, person, phoneType, phoneNumber })] : [],
  );
};

const isTextable = (person: Maybe<ConversationPerson> | undefined): person is ConversationPerson =>
  !!person?.first_name && !!person.last_name && !!person.phone_number;

const hasConversationName = (
  person: Maybe<ConversationPerson> | undefined,
): person is ConversationPerson => person?.id != null && !!(person.first_name || person.last_name);

export const getConversationBoxes = ({ deal }: { deal: Deal | GenDeal }): ConversationBox[] => [
  ...(deal.customer
    ? toPersonBoxes({ role: CustomerContactTypeEnum.Customer, person: deal.customer })
    : []),
  ...(hasConversationName(deal.cobuyer)
    ? toPersonBoxes({ role: CustomerContactTypeEnum.Cobuyer, person: deal.cobuyer })
    : []),
  ...[
    { role: CustomerContactTypeEnum.Contact, person: deal.contact },
    { role: CustomerContactTypeEnum.Second, person: deal.second_contact },
    { role: CustomerContactTypeEnum.OriginalLessee, person: deal.original_lessee },
  ].flatMap(({ role, person }) =>
    isTextable(person)
      ? [
          toBox({
            role,
            person,
            phoneType: PhoneType.Mobile,
            phoneNumber: person.phone_number ?? '',
          }),
        ]
      : [],
  ),
];

export const getBoxUnreadCount = ({
  box,
  byRecipient,
}: {
  box: ConversationBox;
  byRecipient: RecipientThreadUnreadCount[];
}): number => {
  const thread = byRecipient.find(
    ({ recipientId, phoneNumber }) =>
      recipientId === box.personId &&
      isSamePhoneNumber({ phoneNumber, otherPhoneNumber: box.conversation.phone_number }),
  );
  return thread ? thread.count + thread.missedCallCount : 0;
};

export const getActiveConversation = (
  deal: Deal | GenDeal,
  message: Message | GenMessage,
  setActiveConversation: Dispatch<ActiveConversation>,
) => {
  const box = getConversationBoxes({ deal }).find(({ conversation }) =>
    isSamePhoneNumber({ phoneNumber: message.from, otherPhoneNumber: conversation.phone_number }),
  );
  setActiveConversation(box?.conversation ?? emptyConversation);
};
